/**
 * WIPScenarioControls Component
 *
 * View-mode-aware scenario panel for Winter Impact Predictions.
 *
 * Region mode  → 2 sliders (N/S + E/W track shift)
 * District mode → 4 sliders (N/S, E/W, Precipitation, Wind Gust)
 *
 * Sliders update local state; changes are committed on "Run Model".
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle } from "lucide-react";
import AbstractInset, { BaseInsetProps, InsetState } from "./AbstractInset";
import type {
  ScenarioParameterItem,
  ScenarioParameterValues,
  ScenarioParametersResponse,
  WIPScenarioDeviations,
} from "@/api/types/wip";

interface WIPScenarioControlsProps
  extends Omit<BaseInsetProps, "data" | "headers"> {
  viewMode: "region" | "district";
  wipMetadata: ScenarioParametersResponse | null;
  regionMetadata: ScenarioParametersResponse | null;
  values: ScenarioParameterValues;
  onChange: (values: ScenarioParameterValues) => void;
  onRunModel: (values: ScenarioParameterValues) => void;
  isLoading?: boolean;
  trackError?: string | null;
}

const FALLBACK_DISTRICT_PARAMETERS: ScenarioParameterItem[] = [
  { key: "shift_northsouth", available_values: [-100, 0, 100] },
  { key: "shift_eastwest", available_values: [-100, 0, 100] },
  { key: "shift_precipitation", available_values: [-25, 0, 25], unit: "%" },
  { key: "shift_wind_gust", available_values: [-25, 0, 25], unit: "%" },
];

const FALLBACK_REGION_PARAMETERS = FALLBACK_DISTRICT_PARAMETERS.slice(0, 2);

const PARAMETER_LABELS: Record<string, string> = {
  shift_northsouth: "N/S Track Shift",
  shift_eastwest: "E/W Track Shift",
  shift_precipitation: "Precipitation Amount",
  shift_wind_gust: "Wind Gusts",
};

const PARAMETER_DESCRIPTIONS: Record<string, string> = {
  shift_northsouth: "Shift storm path north or south",
  shift_eastwest: "Shift storm path east or west",
  shift_precipitation: "Adjust precipitation amount",
  shift_wind_gust: "Adjust wind gust intensity",
};

function RangeSlider({
  label,
  value,
  availableValues,
  onChange,
  formatValue,
  disabled,
}: {
  label: string;
  value: number;
  availableValues: number[];
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  disabled?: boolean;
}) {
  const values = availableValues.length > 0 ? availableValues : [value];
  const selectedIndex = Math.max(
    0,
    values.findIndex((v) => v === value),
  );
  const displayValue = formatValue ? formatValue(value) : String(value);
  const denominator = Math.max(values.length - 1, 1);
  const percentage = (selectedIndex / denominator) * 100;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-muted-foreground">{label}</Label>
        <span className="text-[11px] font-medium px-1.5 py-0 bg-primary/10 text-primary rounded">
          {displayValue}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={0}
          max={values.length - 1}
          step={1}
          value={selectedIndex}
          onChange={(e) => onChange(values[Number(e.target.value)])}
          disabled={disabled}
          className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:h-3.5
            [&::-webkit-slider-thumb]:w-3.5
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-primary
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:border-2
            [&::-webkit-slider-thumb]:border-white
            [&::-moz-range-thumb]:h-3.5
            [&::-moz-range-thumb]:w-3.5
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-primary
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:border-2
            [&::-moz-range-thumb]:border-white"
          style={{
            background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percentage}%, hsl(var(--muted)) ${percentage}%, hsl(var(--muted)) 100%)`,
          }}
        />
        <div className="flex justify-between text-[9px] text-muted-foreground">
          <span>{formatValue ? formatValue(values[0]) : values[0]}</span>
          <span>
            {formatValue
              ? formatValue(values[values.length - 1])
              : values[values.length - 1]}
          </span>
        </div>
      </div>
    </div>
  );
}

const formatNS = (v: number) => {
  if (v === 0) return "0";
  return v > 0 ? `${v} N` : `${Math.abs(v)} S`;
};

const formatEW = (v: number) => {
  if (v === 0) return "0";
  return v > 0 ? `${v} E` : `${Math.abs(v)} W`;
};

const formatPct = (v: number) => {
  if (v === 0) return "0%";
  return v > 0 ? `+${v}%` : `${v}%`;
};

const formatFallbackLabel = (key: string) =>
  key
    .replace(/^shift_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getFormatter = (key: string, unit?: string | null) => {
  if (key === "shift_northsouth") return formatNS;
  if (key === "shift_eastwest") return formatEW;
  if (unit === "%" || key.includes("precipitation") || key.includes("wind")) {
    return formatPct;
  }
  return undefined;
};

const WIPScenarioControls: React.FC<WIPScenarioControlsProps> = ({
  viewMode,
  wipMetadata,
  regionMetadata,
  values,
  onChange,
  onRunModel,
  loading = false,
  isLoading = false,
  error = null,
  trackError = null,
  position = "top-left",
  width = "340px",
  height = "auto",
  title = "Scenario Controls",
  subtitle,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  panelMode = false,
}) => {
  const [localValues, setLocalValues] =
    useState<ScenarioParameterValues>(values);
  const combinedLoading = loading || isLoading;

  const parameters = useMemo(
    () =>
      (viewMode === "district" ? wipMetadata : regionMetadata)?.parameters ??
      (viewMode === "district"
        ? FALLBACK_DISTRICT_PARAMETERS
        : FALLBACK_REGION_PARAMETERS),
    [viewMode, wipMetadata, regionMetadata],
  );

  const sortedParameters = useMemo(
    () =>
      [...parameters].sort((a, b) => {
        const ao = a.order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.order ?? Number.MAX_SAFE_INTEGER;
        return ao !== bo ? ao - bo : a.key.localeCompare(b.key);
      }),
    [parameters],
  );

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  useEffect(() => {
    setLocalValues((prev) => {
      const next = { ...prev };
      let changed = false;
      sortedParameters.forEach((parameter) => {
        if (next[parameter.key] === undefined) {
          const defaultValue =
            parameter.available_values.find((v) => v === 0) ??
            parameter.available_values[0] ??
            0;
          next[parameter.key] = defaultValue;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sortedParameters]);

  const handleSliderChange = useCallback(
    (key: string, value: number) => {
      setLocalValues((prev) => {
        const next = { ...prev, [key]: value };
        onChange(next);
        return next;
      });
    },
    [onChange],
  );

  const handleRunModel = useCallback(() => {
    onChange(localValues);
    onRunModel(localValues);
  }, [localValues, onChange, onRunModel]);

  const infoItems = sortedParameters.map((parameter) => {
    const label =
      parameter.display_name ??
      PARAMETER_LABELS[parameter.key] ??
      formatFallbackLabel(parameter.key);
    return {
      label,
      desc:
        PARAMETER_DESCRIPTIONS[parameter.key] ??
        `Available values: ${parameter.available_values.join(", ")}`,
    };
  });

  return (
    <AbstractInset
      data={[]}
      loading={combinedLoading}
      error={error}
      position={position}
      width={width}
      height={height}
      title={title}
      subtitle={subtitle}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      className={className}
      panelMode={panelMode}
      containerId="wip-scenario-controls-inset"
      infoTooltip={
        <div className="text-sm space-y-2">
          <p className="font-medium">Scenario Analysis</p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            {infoItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong>: {item.desc}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground italic">
            Click &ldquo;Run Model&rdquo; to apply changes
          </p>
        </div>
      }
    >
      {(_insetState: InsetState) => (
        <div className="p-3 space-y-3">
          {trackError && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-2">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-destructive leading-relaxed">
                {trackError}
              </p>
            </div>
          )}

          <Button
            onClick={handleRunModel}
            disabled={combinedLoading}
            size="sm"
            className="w-full h-8"
          >
            {combinedLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Running...
              </>
            ) : (
              "Run Model"
            )}
          </Button>

          {/* Scenario parameters come from scenario_metadata.parameters. */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              {viewMode === "region" ? "Region Parameters" : "District Parameters"}
            </p>
            {sortedParameters.map((parameter) => {
              const values = parameter.available_values;
              const value =
                localValues[parameter.key] ??
                values.find((v) => v === 0) ??
                values[0] ??
                0;
              const label =
                parameter.display_name ??
                PARAMETER_LABELS[parameter.key] ??
                formatFallbackLabel(parameter.key);
              const unitsSuffix =
                parameter.unit && parameter.unit !== "%" ? ` (${parameter.unit})` : "";
              return (
                <RangeSlider
                  key={parameter.key}
                  label={`${label}${unitsSuffix}`}
                  value={value}
                  availableValues={values}
                  onChange={(v) => handleSliderChange(parameter.key, v)}
                  formatValue={getFormatter(parameter.key, parameter.unit)}
                  disabled={combinedLoading}
                />
              );
            })}
          </div>
        </div>
      )}
    </AbstractInset>
  );
};

export { WIPScenarioControls };
export type { WIPScenarioControlsProps };
